import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

import { useSettings } from '@atlas/core';
import { Button } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';
import Footer from 'components/Footer/Footer';
import SeoPageContext from 'components/SeoPage/SeoPageContext';

const defaultState = {
  content: null,
  error: null,
  loading: true,
  resolved: false,
};

const StaticPage = ({
  CustomFooter = null,
  CustomHeader = null,
  match: {
    params: { slug },
  },
}) => {
  const context = React.useContext(SeoPageContext);

  const t = useTranslate();
  const [state, setState] = useState(defaultState);
  const { lang } = useSettings();
  const componentRef = useRef();
  const handlePrint = useReactToPrint({ content: () => componentRef.current });
  const handleStaticDataLoaded = useCallback(
    res => {
      setState(state => ({
        ...state,
        content: res.data,
        loading: false,
        resolved: true,
      }));
    },
    [setState],
  );

  const handleStaticDataError = useCallback(
    staticError => {
      const { error } = staticError;

      setState(state => ({
        ...state,
        error,
        loading: false,
        resolved: true,
      }));
    },
    [setState],
  );

  const makeContent = content => ({
    __html: DOMPurify.sanitize(content, { KEEP_CONTENT: true }),
  });

  useEffect(() => {
    setState(context.isLoading);
    // eslint-disable-next-line max-len
    if (!context.isLoading && context.currentPage?.slug === slug)
      return handleStaticDataLoaded(context.currentPage);
  }, [lang, slug, handleStaticDataError, handleStaticDataLoaded, context]);

  const renderStaticContent = content => (
    <div
      className="static-content__inner"
      dangerouslySetInnerHTML={makeContent(content)}
      style={{ padding: '10px 16px 16px 16px' }}
    />
  );

  const renderView = () => {
    const { content, error, loading, resolved } = state;

    if (loading) {
      return <div>{t('loading...')}</div>;
    }

    if (resolved) {
      if (error) {
        return <div>{error.message}</div>;
      }

      if (content) {
        return renderStaticContent(content);
      }
    }

    return null;
  };

  const FooterComponent = CustomFooter || Footer;

  return (
    <div className="static-content static-content_page">
      {!!CustomHeader && <CustomHeader contextData={context} />}
      {!CustomHeader && (
        <div className="static-content__header">
          <Button className="secondary" label="print" onClick={handlePrint} />
        </div>
      )}

      <div className="static-content__print" ref={componentRef}>
        <div className="static-content__wrapper">{renderView()}</div>
      </div>

      <FooterComponent />
    </div>
  );
};

StaticPage.propTypes = {
  CustomFooter: PropTypes.elementType,
  CustomHeader: PropTypes.elementType,
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default StaticPage;
